import { ref, onMounted, onUnmounted } from 'vue'
import useCheckScreen from '@/composables/useCheckScreen'
import { useAuthSteps } from './useAuthSteps'
import { api } from '@/helpers/api'

const URLS = {
    xhr: {
        fetch: {
            user: '/api/profile/v1/settings/user',
            artist: {
                info: '/api/profile/v1/user-profile/artist-info',
                media: '/api/profile/v1/user-profile/artist-medias',
                social: '/api/profile/v1/user-profile/artist-social-links',
                music: '/api/profile/audios'
            },
            venue: {
                info: '/api/profile/v1/user-profile/venue-info',
                media: '/api/profile/v1/user-profile/venue-medias',
                social: '/api/profile/v1/user-profile/venue-social-links'
            }
        }
    }
}
const MODES = {
    None: Symbol(''),
    Login: Symbol('login'),
    ProfileRegister: Symbol('profile-register'),
    EventRegister: Symbol('event-register')
}
const ARTIST_INFO_DATA = {
    bandName: 'band_name',
    email: 'band_email',
    homeLocation: 'home_location',
    lat: 'lat',
    lng: 'lon',
    keywords: 'keyword'
}
const VENUE_INFO_DATA = {
    venueName: 'venue_name',
    email: 'venue_email',
    address: 'address',
    website: 'web_site',
    lat: 'lat',
    lng: 'lon',
    venueType: 'venue_type',
    keywords: 'keyword'
}
const mode = ref('')
const load = ref(false)
const authedRegister = ref(false)
const showAuthModal = ref(false)
const userValues = ref({
    id: null,
    venueSlug: '',
    artistSlug: '',
    email: '',
    firstName: '',
    bandName: '',
    venueName: '',
    homeLocation: '',
    address: '',
    website: '',
    city: '',
    state: '',
    zip: '',
    lat: '',
    lng: '',
    venueType: '',
    keywords: '',
    socials: [],
    medias: [],
    audios: []
})

export const useAuth = () => {
    const { isMobile } = useCheckScreen()
    const {
        stepIndex,
        authSteps,
        loginSteps,
        profileRegisterSteps,
        eventRegisterSteps,
        updateStepIndex,
        updateProfileOrEventSteps
    } = useAuthSteps({
        MODES,
        mode,
        userValues,
        toggleAuthModal,
        fetchProfileInfo
    })

    ;(() => {
        _scrollDownIfTop()
    })()

    onMounted(() => {
        window.addEventListener('beforeunload', _handleunload)
    })
    onUnmounted(() => {
        window.removeEventListener('beforeunload', _handleunload)
    })

    function toggleAuthModal(state) {
        if (state) {
            showAuthModal.value = true
            document.body.classList.add('modal-open')
        } else {
            showAuthModal.value = false
            document.body.classList.remove('modal-open')
        }
    }
    function handleShowLoginModal() {
        if (!showAuthModal.value) {
            toggleAuthModal(true)
        }

        if (stepIndex.value > 1) {
            handleShowRegisterModal({
                registerMode: mode.value === MODES.ProfileRegister ? 'profile' : 'event'
            })
        } else {
            updateStepIndex(loginSteps, 'login-welcome')
            mode.value = MODES.Login
        }
    }
    function handleShowRegisterModal({ registerMode } = { registerMode: 'profile' }) {
        if (!showAuthModal.value) toggleAuthModal(true)

        switch (registerMode) {
            case 'profile':
                mode.value = MODES.ProfileRegister
                break
            case 'event':
                mode.value = MODES.EventRegister
                break
            default:
                return
        }
    }
    function handleShowProfileRegister() {
        localStorage.setItem('auth-modal', 'auth')
        localStorage.setItem('role', 'none')
        localStorage.setItem('register-mode', MODES.ProfileRegister.toString())
        handleShowLoginModal()
    }
    function handleAuthOverlayClick() {
        toggleAuthModal(false)
    }
    async function fetchUserInfo() {
        const data = await api({
            method: 'get',
            url: URLS.xhr.fetch.user
        })
        try {
            userValues.value.id = data.data[0].id
            userValues.value.firstName = data.data[0].first_name
            userValues.value.email = data.data[0].email
            userValues.value.venueSlug = data.data[0].venueSlug
            userValues.value.artistSlug = data.data[0].artistSlug
            return data.data[0]
        } catch {
            console.error('API or Social Authentication Failure')
        }
    }
    async function fetchProfileInfo() {
        try {
            load.value = true
            await fetchUserInfo()
            if (userValues.value.venueSlug) {
                _populateUserValues(await _fetchVenueInfo(), 'venue')
            } else if (userValues.value.artistSlug) {
                _populateUserValues(await _fetchArtistInfo(), 'artist')
            }
        } finally {
            load.value = false
        }
    }
    function _handleunload() {
        load.value = true
    }
    function _scrollDownIfTop() {
        if (window.top.scrollY === 0 && isMobile.value) {
            window.scrollTo({
                top: 80,
                behavior: 'smooth'
            })
        }
    }
    function _populateUserValues(data, role) {
        if (!data) return

        if (data.medias && data.medias.length) _populateMediasData(data.medias)
        if (data.audios && data.audios.length) _populateAudiosData(data.audios)
        if (data.socials && data.socials.length) _populateSocialData(data.socials)
        if (data.home_location) _populateCityStateZip(data.home_location)

        if (role === 'artist') {
            for (const [key, val] of Object.entries(ARTIST_INFO_DATA)) {
                if (data[val]) {
                    userValues.value[key] = data[val]
                } else if (data[key]) {
                    userValues.value[key] = data[key]
                }
            }
        } else if (role === 'venue') {
            for (const [key, val] of Object.entries(VENUE_INFO_DATA)) {
                if (data[val]) {
                    userValues.value[key] = data[val]
                } else if (data[key]) {
                    userValues.value[key] = data[key]
                }
            }
        }
    }
    function _populateSocialData(socials) {
        userValues.value.socials = socials.map((s) => ({ type: s.type, id: s.id }))
    }
    function _populateCityStateZip(location) {
        const address = location.split(',')
        const city = address[0]
        const stateZip = address[1].trim().split(' ')

        const state = stateZip[0]
        const zip = stateZip[1]

        userValues.value.city = city
        userValues.value.state = state
        userValues.value.zip = zip
    }
    function _populateMediasData(medias) {
        userValues.value.medias = medias
    }
    function _populateAudiosData(audios) {
        userValues.value.audios = audios
    }
    async function _fetchVenueInfo() {
        const info = await api({
            method: 'get',
            url: `${URLS.xhr.fetch.venue.info}/${userValues.value.id}`
        })
        const media = await api({
            method: 'get',
            url: `${URLS.xhr.fetch.venue.media}/${userValues.value.id}`
        })
        const social = await api({
            method: 'get',
            url: `${URLS.xhr.fetch.venue.social}/${userValues.value.id}`
        })
        return {
            ...info.data,
            medias: media.data,
            socials: social.data
        }
    }
    async function _fetchArtistInfo() {
        const info = await api({
            method: 'get',
            url: `${URLS.xhr.fetch.artist.info}/${userValues.value.id}`
        })
        const media = await api({
            method: 'get',
            url: `${URLS.xhr.fetch.artist.media}/${userValues.value.id}`
        })
        const social = await api({
            method: 'get',
            url: `${URLS.xhr.fetch.artist.social}/${userValues.value.id}`
        })
        const audio = await api({
            method: 'get',
            url: `${URLS.xhr.fetch.artist.music}/${userValues.value.id}`
        })

        return {
            ...info.data,
            medias: media.data,
            socials: social.data,
            audios: audio.data
        }
    }

    return {
        MODES,
        ARTIST_INFO_DATA,
        VENUE_INFO_DATA,
        mode,
        load,
        userValues,
        showAuthModal,

        authedRegister,
        stepIndex,
        authSteps,
        loginSteps,
        profileRegisterSteps,
        eventRegisterSteps,
        toggleAuthModal,
        updateProfileOrEventSteps,

        fetchUserInfo,
        fetchProfileInfo,
        handleShowLoginModal,
        handleShowRegisterModal,
        handleShowProfileRegister,
        handleAuthOverlayClick
    }
}
